import React from 'react';
import { Truck, CheckCircle, Phone, Mail, Globe, MapPin, CreditCard, Users, Zap, Shield, Star, Gift } from 'lucide-react';

const FolderDivulgacao = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
        }
        @page { size: A4; margin: 0; }
      `}</style>

      {/* Botão Imprimir */}
      <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
        <button 
          onClick={() => window.print()}
          className="bg-orange-500 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-orange-600 flex items-center gap-2 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Imprimir / Salvar PDF
        </button>
        <button 
          onClick={() => window.history.back()}
          className="bg-gray-500 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-gray-600 font-medium"
        >
          Voltar
        </button>
      </div>

      {/* PÁGINA 1 - FRENTE */}
      <div className="w-[210mm] min-h-[297mm] mx-auto bg-white relative overflow-hidden">
        {/* Header com gradiente */}
        <div className="bg-gradient-to-br from-orange-600 via-orange-700 to-amber-700 text-white px-10 py-12 relative">
          {/* Elementos decorativos */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            {/* Logo */}
            <div className="flex items-center gap-4 mb-8">
              <img src="/logo-rota017.png" alt="Rota 017" className="w-16 h-16 rounded-2xl shadow-lg object-cover" />
              <div>
                <h1 className="text-4xl font-bold tracking-tight">Rota 017</h1>
                <p className="text-orange-200 text-lg">Seu pedido, nossa rota!</p>
              </div>
            </div>
            
            {/* Headline */}
            <h2 className="text-5xl font-bold leading-tight mb-4">
              A plataforma que<br />
              <span className="text-yellow-300">conecta</span> empresas<br />
              a entregadores
            </h2>
            
            <p className="text-xl text-orange-100 max-w-lg">
              Gestão completa de entregas B2B. Sem vínculo empregatício. 
              100% pré-pago. Tecnologia que simplifica.
            </p>
          </div>
        </div>

        {/* Segmentos Atendidos */}
        <div className="px-10 py-6 bg-slate-50">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Atendemos Diversos Segmentos</h3>
          <div className="grid grid-cols-6 gap-3">
            {[
              { icon: "🍕", name: "Restaurantes" },
              { icon: "💊", name: "Farmácias" },
              { icon: "🛒", name: "Supermercados" },
              { icon: "🌸", name: "Floristas" },
              { icon: "🐕", name: "Pet Shops" },
              { icon: "📦", name: "E-commerce" }
            ].map((seg, i) => (
              <div key={i} className="text-center bg-white rounded-lg p-2 shadow-sm">
                <span className="text-xl">{seg.icon}</span>
                <p className="text-xs font-medium text-gray-700 mt-1">{seg.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Seção de Benefícios */}
        <div className="px-10 py-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-orange-600" />
            Por que escolher a Rota 017?
          </h3>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">Entregas em Tempo Real</h4>
                <p className="text-gray-600 text-sm">Acompanhe cada entrega com status atualizado instantaneamente.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">Segurança Garantida</h4>
                <p className="text-gray-600 text-sm">Código de confirmação em cada entrega. Documentação validada.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">Rotas Integradas</h4>
                <p className="text-gray-600 text-sm">GPS e rotas otimizadas direto no app. Google Maps e Waze.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">Gestão de Equipe</h4>
                <p className="text-gray-600 text-sm">Cadastre funcionários e gerencie sua frota com facilidade.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Planos lado a lado */}
        <div className="px-10 py-8 bg-gray-50">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Planos Acessíveis</h3>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Plano Cliente */}
            <div className="bg-white rounded-2xl p-6 border-2 border-orange-200 shadow-sm">
              <div className="text-center mb-4">
                <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-medium">
                  Para Empresas
                </span>
                <h4 className="text-xl font-bold text-gray-800 mt-2">Plano Cliente</h4>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">R$ 149</span>
                  <span className="text-gray-500">/mês</span>
                </div>
                <p className="text-sm text-orange-600 mt-1">+ R$ 2,90 por entrega</p>
              </div>
              
              <ul className="space-y-2">
                {['Entregas ilimitadas', 'Múltiplos entregadores', 'Relatórios completos', 'Suporte prioritário'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Plano Entregador */}
            <div className="bg-white rounded-2xl p-6 border-2 border-green-200 shadow-sm">
              <div className="text-center mb-4">
                <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-medium">
                  Para Entregadores
                </span>
                <h4 className="text-xl font-bold text-gray-800 mt-2">Plano Entregador</h4>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">R$ 49,90</span>
                  <span className="text-gray-500">/semana</span>
                </div>
                <p className="text-sm text-green-600 mt-1">Sem taxa por entrega!</p>
              </div>
              
              <ul className="space-y-2">
                {['Entregas ilimitadas', 'App com rotas', 'Cadastro de funcionários', 'Notificações em tempo real'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bônus Entregador */}
        <div className="px-10 py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Gift className="w-6 h-6" />
            <h3 className="text-xl font-bold">Bônus Exclusivos para Entregadores</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <p className="text-2xl mb-1">🏥</p>
              <p className="text-sm font-medium">Telemedicina Gratuita</p>
              <p className="text-xs text-green-100">Para assinantes</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <p className="text-2xl mb-1">🛢️</p>
              <p className="text-sm font-medium">Troca de Óleo</p>
              <p className="text-xs text-green-100">300 entregas/mês</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <p className="text-2xl mb-1">⚙️</p>
              <p className="text-sm font-medium">Troca de Relação</p>
              <p className="text-xs text-green-100">600+ entregas/mês</p>
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="px-10 py-6 bg-gray-900 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover" />
              <div>
                <p className="font-bold">Rota 017</p>
                <p className="text-sm text-gray-400">Seu pedido, nossa rota!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-orange-400" />
                <span>www.rota017.com.br</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-orange-400" />
                <span>(17) 99999-9999</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-orange-400" />
                <span>contato@rota017.com.br</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quebra de página para impressão */}
      <div className="page-break" />

      {/* PÁGINA 2 - VERSO (Como Funciona) */}
      <div className="w-[210mm] min-h-[297mm] mx-auto bg-white relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-10 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-rota017.png" alt="Rota 017" className="w-12 h-12 rounded-xl object-cover" />
              <div>
                <h1 className="text-2xl font-bold">Rota 017</h1>
                <p className="text-slate-400">Como Funciona</p>
              </div>
            </div>
            <p className="text-orange-400 font-medium">Simples, Rápido e Seguro</p>
          </div>
        </div>

        {/* Fluxo do Cliente */}
        <div className="px-10 py-8">
          <h3 className="text-xl font-bold text-orange-600 mb-6 flex items-center gap-2">
            <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">1</span>
            Para a Empresa (Cliente)
          </h3>
          
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { step: '1', title: 'Cadastre-se', desc: 'Crie sua conta e envie os documentos' },
              { step: '2', title: 'Crie Entrega', desc: 'Informe destino e valor' },
              { step: '3', title: 'Acompanhe', desc: 'Veja o status em tempo real' },
              { step: '4', title: 'Confirme', desc: 'Receba o código de entrega' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                  {item.step}
                </div>
                <h4 className="font-bold text-gray-800 text-sm">{item.title}</h4>
                <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-green-600 mb-6 flex items-center gap-2">
            <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">2</span>
            Para o Entregador
          </h3>
          
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { step: '1', title: 'Cadastre-se', desc: 'Crie conta e valide documentos' },
              { step: '2', title: 'Receba Pedidos', desc: 'Notificação instantânea' },
              { step: '3', title: 'Faça a Rota', desc: 'GPS integrado no app' },
              { step: '4', title: 'Confirme Entrega', desc: 'Insira o código e finalize' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                  {item.step}
                </div>
                <h4 className="font-bold text-gray-800 text-sm">{item.title}</h4>
                <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Diferenciais */}
        <div className="px-10 py-8 bg-slate-50">
          <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Nossos Diferenciais</h3>
          
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-5 text-center shadow-sm">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-7 h-7 text-orange-600" />
              </div>
              <h4 className="font-bold text-gray-800 mb-1">100% Pré-Pago</h4>
              <p className="text-sm text-gray-500">Sem surpresas. Você controla seus custos.</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 text-center shadow-sm">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-800 mb-1">Sem Vínculo</h4>
              <p className="text-sm text-gray-500">Apenas tecnologia. Sem responsabilidades trabalhistas.</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 text-center shadow-sm">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-7 h-7 text-green-600" />
              </div>
              <h4 className="font-bold text-gray-800 mb-1">App Instalável</h4>
              <p className="text-sm text-gray-500">Funciona como app nativo. Android e iOS.</p>
            </div>
          </div>
        </div>

        {/* Depoimentos / Social Proof */}
        <div className="px-10 py-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">O Que Dizem Nossos Usuários</h3>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-orange-50 rounded-xl p-5 border border-orange-100">
              <div className="flex mb-2">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-orange-400 fill-current" />)}
              </div>
              <p className="text-gray-700 text-sm italic mb-3">
                "Reduzi meus custos com entrega em 40%. O controle em tempo real é incrível!"
              </p>
              <p className="text-sm font-medium text-gray-800">Maria Silva</p>
              <p className="text-xs text-gray-500">Farmácia Central</p>
            </div>
            
            <div className="bg-green-50 rounded-xl p-5 border border-green-100">
              <div className="flex mb-2">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-green-400 fill-current" />)}
              </div>
              <p className="text-gray-700 text-sm italic mb-3">
                "Os bônus são reais! Já ganhei 3 trocas de óleo. Recomendo demais!"
              </p>
              <p className="text-sm font-medium text-gray-800">Carlos Motoboy</p>
              <p className="text-xs text-gray-500">Entregador há 8 meses</p>
            </div>
          </div>
        </div>

        {/* QR Code e CTA */}
        <div className="px-10 py-8 bg-gradient-to-r from-orange-600 to-amber-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Comece Agora Mesmo!</h3>
              <p className="text-orange-100 mb-4">Cadastro rápido. Aprovação em até 24h.</p>
              <div className="flex items-center gap-4">
                <div className="bg-white text-orange-600 px-6 py-3 rounded-lg font-bold">
                  www.rota017.com.br
                </div>
                <div className="text-sm">
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> (17) 99999-9999</p>
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> contato@rota017.com.br</p>
                </div>
              </div>
            </div>
            
            {/* Placeholder para QR Code */}
            <div className="bg-white p-3 rounded-xl">
              <div className="w-28 h-28 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Globe className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">QR Code</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="px-10 py-4 bg-gray-900 text-white text-center text-sm">
          <p className="text-gray-400">
            © 2026 Rota 017 • CNPJ: 00.000.000/0001-00 • Seu pedido, nossa rota! • Tecnologia que conecta
          </p>
        </div>
      </div>
    </div>
  );
};

export default FolderDivulgacao;
